import { QuestionCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import { DateHelper } from 'src/utils/DateHelper';
import { NumberHelper } from 'src/utils/NumberHelper';
import styled from 'styled-components';

export type DataViewFormatValue =
    | 'percent'
    | 'number'
    | 'currency-sar'
    | 'date'
    | 'dateTime'
    | 'dateTimeSeconds'
    | ((value: any) => React.ReactNode);

export interface DataViewProps {
    label?: React.ReactNode;
    value?: any;
    suffix?: React.ReactNode;
    className?: string;
    formatValue?: DataViewFormatValue;
    help?: React.ReactNode;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getValue = (value: any): any => {
    if (_.isNil(value)) {
        return '—';
    }

    return value;
};

// eslint-disable-next-line complexity
const formatValueByType = (
    value: any,
    format: DataViewFormatValue,
): React.ReactNode => {
    if (typeof format === 'function') {
        return format(value);
    }

    if (_.isNil(value)) {
        return '—';
    }

    switch (format) {
        case 'number': {
            if (typeof value === 'number') {
                return NumberHelper.formatThousandSeparator(value);
            }

            return value;
        }
        case 'percent': {
            if (typeof value === 'number') {
                return NumberHelper.formatToPercent(value);
            }

            return value;
        }
        case 'currency-sar': {
            if (typeof value === 'number') {
                return `SAR ${NumberHelper.formatThousandSeparator(value)}`;
            }

            return value;
        }
        case 'date': {
            return DateHelper.formatDate(value);
        }
        case 'dateTime': {
            return DateHelper.formatDateTime(value);
        }
        case 'dateTimeSeconds': {
            return moment(value).format('DD/MM/YYYY HH:mm:ss');
        }
        default: {
            return value;
        }
    }
};

export const DataView = ({
    label,
    value,
    suffix,
    className,
    formatValue,
    help,
}: DataViewProps) => {
    const formattedValue = formatValue
        ? formatValueByType(value, formatValue)
        : getValue(value);

    return (
        <div
            className={classNames('data-view', className)}
            data-testid="data-view"
        >
            {label && (
                <StyledLabel className="data-view__label">
                    {label}{' '}
                    {help && (
                        <Tooltip title={<span>{help}</span>}>
                            <QuestionCircleOutlined />
                        </Tooltip>
                    )}
                </StyledLabel>
            )}
            {formattedValue && (
                <StyledValue className="data-view__value">
                    {formattedValue}
                </StyledValue>
            )}
            {suffix && (
                <StyledSuffix className="data-view__suffix">
                    {suffix}
                </StyledSuffix>
            )}
        </div>
    );
};

const StyledLabel = styled.div`
    font-size: 0.875rem;
    color: var(--label-color);
    margin-bottom: 0.25rem;
`;

const StyledValue = styled.div`
    font-size: 1rem;
    color: var(--text-color);
    font-weight: 500;
`;

const StyledSuffix = styled.div`
    font-size: 0.875rem;
    color: var(--label-color);
    margin-left: 0.25rem;
`;
